import { TXsollaSubscriptionOperation } from '../../../common/xsolla';
import { TXsollaSubscriptionTier } from '../../../common/xsollaConfig';
import { getPremiumEntrySource } from './premiumEntrySource';
import { IXsollaWidgetPaymentInfo, TXsollaNormalizedWidgetEvent } from './xsollaWidgetAdapter';

export type TPremiumCheckoutSurface =
  | 'premium_page'
  | 'subscription_settings'
  | 'checkout_success'
  | 'subscription_options';

export type TPremiumBillingPeriod = 'monthly' | 'annual';
export type TPremiumRailPosition = 'top' | 'bottom';
export type TPremiumCheckoutProvider = 'xsolla' | 'patreon';
export type TPremiumInteractionTarget = 'cta_button' | 'card_body' | 'billing_toggle';
export type TPremiumPaymentMethodFamily =
  | 'alipay'
  | 'wechat_pay'
  | 'credit_card'
  | 'paypal'
  | 'wallet'
  | 'bank_transfer'
  | 'saved_method'
  | 'other'
  | 'unknown';
export type TPremiumCheckoutStage =
  | 'widget_open'
  | 'widget_loaded'
  | 'payment_method_select'
  | 'payment_submit'
  | 'status_pending'
  | 'status_success'
  | 'status_error'
  | 'closed'
  | 'error'
  | 'other';

export type TGaClientContext = {
  gaClientId?: string;
  gaSessionId?: string;
};

export type TPremiumAnalyticsItem = {
  item_id: string;
  item_name: string;
  item_brand: 'Raider.IO';
  item_category: 'Premium';
  item_variant: TPremiumBillingPeriod;
  price?: number;
  currency?: string;
  index?: number;
  quantity?: number;
};

type TTrackableParam = string | number | boolean | TPremiumAnalyticsItem[] | undefined | null;
type TPremiumTrackParams = Record<string, TTrackableParam>;

const GA_ALLOWED_PARAM_KEYS = new Set([
  'checkout_surface',
  'checkout_stage',
  'tier',
  'billing_period',
  'subscription_operation',
  'change_direction',
  'interaction_target',
  'entry_source',
  'market_country',
  'payment_method_family',
  'outcome',
  'cancel_reason_code',
  'activation_stage',
  'currency',
  'value',
  'items',
  'item_list_id',
  'item_list_name',
  'payment_type',
  'rail_position',
  'checkout_provider',
  'section_id',
  'percent_scrolled'
]);

const GA_BLOCKED_PARAM_KEYS = new Set([
  'checkout_attempt_id',
  'payment_external_id',
  'order_id',
  'subscription_id',
  'xsolla_user_id',
  'bnet_id',
  'email',
  'error_message',
  'reason_text'
]);

function isPrimitiveTrackValue(value: TTrackableParam): value is string | number | boolean | TPremiumAnalyticsItem[] {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || Array.isArray(value);
}

function sanitizeGaParams(params: TPremiumTrackParams = {}): Record<string, string | number | boolean | TPremiumAnalyticsItem[]> {
  const result: Record<string, string | number | boolean | TPremiumAnalyticsItem[]> = {};

  for (const [key, value] of Object.entries(params)) {
    if (GA_BLOCKED_PARAM_KEYS.has(key) || !GA_ALLOWED_PARAM_KEYS.has(key) || !isPrimitiveTrackValue(value)) {
      continue;
    }

    if (Array.isArray(value)) {
      result[key] = value.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        item_brand: item.item_brand,
        item_category: item.item_category,
        item_variant: item.item_variant,
        ...(typeof item.price === 'number' ? { price: item.price } : {}),
        ...(item.currency ? { currency: item.currency } : {}),
        ...(typeof item.index === 'number' ? { index: item.index } : {}),
        ...(typeof item.quantity === 'number' ? { quantity: item.quantity } : {})
      }));
      continue;
    }

    result[key] = value;
  }

  return result;
}

export function trackPremiumAnalyticsEvent(eventName: string, params: TPremiumTrackParams = {}): void {
  if (typeof RIO_TRACK_EVENT !== 'function') {
    return;
  }

  RIO_TRACK_EVENT('event', eventName, sanitizeGaParams(params));
}

function getTierName(tier: TXsollaSubscriptionTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getBillingPeriod(isAnnual: boolean): TPremiumBillingPeriod {
  return isAnnual ? 'annual' : 'monthly';
}

export function buildPremiumAnalyticsItem(params: {
  tier: TXsollaSubscriptionTier;
  billingPeriod: TPremiumBillingPeriod;
  price?: number;
  currency?: string;
  index?: number;
}): TPremiumAnalyticsItem {
  const { tier, billingPeriod, price, currency, index } = params;

  return {
    item_id: `raiderio_premium_${tier}_${billingPeriod}`,
    item_name: `Raider.IO Premium ${getTierName(tier)}`,
    item_brand: 'Raider.IO',
    item_category: 'Premium',
    item_variant: billingPeriod,
    quantity: 1,
    ...(typeof price === 'number' ? { price } : {}),
    ...(currency ? { currency } : {}),
    ...(typeof index === 'number' ? { index } : {})
  };
}

export function createCheckoutAttemptId(): string {
  const timestamp = Date.now().toString(36);
  const random = typeof window !== 'undefined' && window.crypto?.getRandomValues
    ? Array.from(window.crypto.getRandomValues(new Uint32Array(2)))
      .map((value) => value.toString(36))
      .join('')
    : Math.random().toString(36).slice(2);

  return `xsolla_${timestamp}_${random}`.slice(0, 96);
}

function getCookieValue(cookieString: string, cookieName: string): string | null {
  const prefix = `${cookieName}=`;
  const entry = cookieString
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));

  return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
}

export function getGaClientContext(cookieString?: string): TGaClientContext {
  if (typeof document === 'undefined' && cookieString === undefined) {
    return {};
  }

  const cookies = cookieString ?? document.cookie ?? '';
  const gaCookie = getCookieValue(cookies, '_ga');
  const clientMatch = gaCookie?.match(/^GA\d+\.\d+\.(.+)$/);
  const gaClientId = clientMatch?.[1] || undefined;
  const sessionCookie = cookies
    .split(';')
    .map((value) => value.trim())
    .find((value) => /^_ga_[A-Z0-9]+=/.test(value));
  const sessionCookieValue = sessionCookie ? decodeURIComponent(sessionCookie.split('=').slice(1).join('=')) : '';
  const sessionMatch = sessionCookieValue.match(/(?:^|[.$])s(\d+)(?:[.$]|$)/);

  return {
    ...(gaClientId ? { gaClientId } : {}),
    ...(sessionMatch?.[1] ? { gaSessionId: sessionMatch[1] } : {})
  };
}

export function getPaymentMethodFamily(paymentMethod?: string | null): TPremiumPaymentMethodFamily {
  const value = paymentMethod?.toLowerCase().trim();
  if (!value) {
    return 'unknown';
  }

  if (value.includes('alipay')) {
    return 'alipay';
  }
  if (value.includes('wechat') || value.includes('weixin')) {
    return 'wechat_pay';
  }
  if (value.includes('card') || value.includes('visa') || value.includes('mastercard') || value.includes('amex')) {
    return 'credit_card';
  }
  if (value.includes('paypal')) {
    return 'paypal';
  }
  if (value.includes('wallet')) {
    return 'wallet';
  }
  if (value.includes('bank') || value.includes('transfer')) {
    return 'bank_transfer';
  }
  if (value.includes('saved')) {
    return 'saved_method';
  }

  return 'other';
}

export function getCheckoutStageFromWidgetEvent(event: TXsollaNormalizedWidgetEvent): TPremiumCheckoutStage {
  if (event.type === 'open') {
    return 'widget_open';
  }
  if (event.type === 'payment_info') {
    return 'payment_submit';
  }
  if (event.type === 'close') {
    return 'closed';
  }
  if (event.type === 'pending') {
    return 'status_pending';
  }
  if (event.type === 'success') {
    return 'status_success';
  }
  if (event.type === 'error') {
    return 'status_error';
  }
  if (event.type === 'stage') {
    const command = event.command.toLowerCase();
    if (command.includes('load')) {
      return 'widget_loaded';
    }
    if (command.includes('payment-method')) {
      return 'payment_method_select';
    }
    if (command.includes('click-btn-pay') || command.includes('payment-submit')) {
      return 'payment_submit';
    }
    if (command.includes('error')) {
      return 'error';
    }
  }

  return 'other';
}

export function trackPremiumPageView(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  checkoutProvider: TPremiumCheckoutProvider;
  marketCountry?: string | null;
}): void {
  trackPremiumAnalyticsEvent('premium_page_view', {
    checkout_surface: params.checkoutSurface,
    checkout_provider: params.checkoutProvider,
    entry_source: getPremiumEntrySource() || undefined,
    market_country: params.marketCountry || undefined
  });
}

export function trackPremiumPlanImpression(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  marketCountry?: string | null;
  items: TPremiumAnalyticsItem[];
  railPosition?: TPremiumRailPosition;
  checkoutProvider?: TPremiumCheckoutProvider;
}): void {
  trackPremiumAnalyticsEvent('view_item_list', {
    checkout_surface: params.checkoutSurface,
    market_country: params.marketCountry || undefined,
    rail_position: params.railPosition,
    checkout_provider: params.checkoutProvider,
    item_list_id: 'premium_plans',
    item_list_name: 'Premium Plans',
    items: params.items
  });
}

export function trackPremiumPlanSelect(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier: TXsollaSubscriptionTier;
  billingPeriod: TPremiumBillingPeriod;
  interactionTarget: TPremiumInteractionTarget;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  item: TPremiumAnalyticsItem;
  railPosition?: TPremiumRailPosition;
}): void {
  trackPremiumAnalyticsEvent('select_item', {
    checkout_surface: params.checkoutSurface,
    tier: params.tier,
    billing_period: params.billingPeriod,
    interaction_target: params.interactionTarget,
    entry_source: getPremiumEntrySource() || undefined,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    rail_position: params.railPosition,
    item_list_id: 'premium_plans',
    item_list_name: 'Premium Plans',
    items: [params.item]
  });
}

export function trackPremiumBillingPeriodSelect(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  billingPeriod: TPremiumBillingPeriod;
  marketCountry?: string | null;
}): void {
  trackPremiumAnalyticsEvent('premium_billing_period_select', {
    checkout_surface: params.checkoutSurface,
    billing_period: params.billingPeriod,
    interaction_target: 'billing_toggle',
    market_country: params.marketCountry || undefined
  });
}

export function trackPremiumBeginCheckout(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier: TXsollaSubscriptionTier;
  billingPeriod: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  item: TPremiumAnalyticsItem;
  railPosition?: TPremiumRailPosition;
}): void {
  trackPremiumAnalyticsEvent('begin_checkout', {
    checkout_surface: params.checkoutSurface,
    tier: params.tier,
    billing_period: params.billingPeriod,
    entry_source: getPremiumEntrySource() || undefined,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    rail_position: params.railPosition,
    currency: params.item.currency,
    value: params.item.price,
    items: [params.item]
  });
}

export function trackPremiumCheckoutWidgetOpen(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier?: TXsollaSubscriptionTier;
  billingPeriod?: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
}): void {
  trackPremiumAnalyticsEvent('xsolla_checkout_widget_open', {
    checkout_surface: params.checkoutSurface,
    checkout_stage: 'widget_open',
    tier: params.tier,
    billing_period: params.billingPeriod,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation
  });
}

export function trackPremiumCheckoutWidgetStage(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  checkoutStage: TPremiumCheckoutStage;
  tier?: TXsollaSubscriptionTier;
  billingPeriod?: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  paymentMethodFamily?: TPremiumPaymentMethodFamily;
  outcome?: string;
}): void {
  // The widget emits far more lifecycle commands than map to a stage we can act on, and an
  // unrecognized one carries no information beyond "something happened". Dropping them keeps
  // this from being the highest-volume premium event on the site.
  if (params.checkoutStage === 'other') {
    return;
  }

  trackPremiumAnalyticsEvent('xsolla_checkout_widget_stage', {
    checkout_surface: params.checkoutSurface,
    checkout_stage: params.checkoutStage,
    tier: params.tier,
    billing_period: params.billingPeriod,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    payment_method_family: params.paymentMethodFamily,
    outcome: params.outcome
  });
}

export function trackPremiumAddPaymentInfo(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier?: TXsollaSubscriptionTier;
  billingPeriod?: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  paymentInfo?: IXsollaWidgetPaymentInfo;
  item?: TPremiumAnalyticsItem;
}): void {
  const paymentMethodFamily = getPaymentMethodFamily(String(
    params.paymentInfo?.payment_method ||
    params.paymentInfo?.payment_method_name ||
    params.paymentInfo?.paymentMethod ||
    ''
  ));

  trackPremiumAnalyticsEvent('add_payment_info', {
    checkout_surface: params.checkoutSurface,
    checkout_stage: 'payment_submit',
    tier: params.tier,
    billing_period: params.billingPeriod,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    payment_method_family: paymentMethodFamily,
    payment_type: paymentMethodFamily === 'unknown' ? undefined : paymentMethodFamily,
    currency: params.item?.currency,
    value: params.item?.price,
    items: params.item ? [params.item] : undefined
  });
}

export function trackPremiumCheckoutClosed(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier?: TXsollaSubscriptionTier;
  billingPeriod?: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('xsolla_checkout_closed', {
    checkout_surface: params.checkoutSurface,
    checkout_stage: 'closed',
    tier: params.tier,
    billing_period: params.billingPeriod,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    outcome: params.outcome
  });
}

export function trackPremiumCheckoutError(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier?: TXsollaSubscriptionTier;
  billingPeriod?: TPremiumBillingPeriod;
  marketCountry?: string | null;
  operation?: TXsollaSubscriptionOperation;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('xsolla_checkout_error', {
    checkout_surface: params.checkoutSurface,
    checkout_stage: 'error',
    tier: params.tier,
    billing_period: params.billingPeriod,
    market_country: params.marketCountry || undefined,
    subscription_operation: params.operation,
    outcome: params.outcome || 'failed'
  });
}

export function trackPremiumCheckoutActivationStatus(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  activationStage: string;
  tier?: string | null;
  billingPeriod?: string | null;
  operation?: string | null;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('premium_checkout_activation_status', {
    checkout_surface: params.checkoutSurface,
    activation_stage: params.activationStage,
    tier: params.tier || undefined,
    billing_period: params.billingPeriod || undefined,
    subscription_operation: params.operation || undefined,
    outcome: params.outcome
  });
}

export function trackPremiumPlanChangeIntent(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  tier: TXsollaSubscriptionTier;
  billingPeriod: TPremiumBillingPeriod;
  changeDirection?: string | null;
  marketCountry?: string | null;
}): void {
  trackPremiumAnalyticsEvent('premium_plan_change_intent', {
    checkout_surface: params.checkoutSurface,
    tier: params.tier,
    billing_period: params.billingPeriod,
    subscription_operation: 'change_plan',
    change_direction: params.changeDirection || undefined,
    market_country: params.marketCountry || undefined
  });
}

export function trackPremiumSubscriptionChange(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  operation?: string;
  changeDirection?: string | null;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('premium_subscription_change', {
    checkout_surface: params.checkoutSurface,
    subscription_operation: params.operation,
    change_direction: params.changeDirection || undefined,
    outcome: params.outcome
  });
}

export function trackPremiumSubscriptionCancelStart(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('premium_subscription_cancel_start', {
    checkout_surface: params.checkoutSurface,
    outcome: params.outcome
  });
}

export function trackPremiumSubscriptionCancelReasonSelect(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  cancelReasonCode: string;
}): void {
  trackPremiumAnalyticsEvent('premium_subscription_cancel_reason_select', {
    checkout_surface: params.checkoutSurface,
    cancel_reason_code: params.cancelReasonCode
  });
}

export function trackPremiumSubscriptionCancelSubmit(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  cancelReasonCode?: string;
  outcome?: string;
}): void {
  trackPremiumAnalyticsEvent('premium_subscription_cancel_submit', {
    checkout_surface: params.checkoutSurface,
    cancel_reason_code: params.cancelReasonCode,
    outcome: params.outcome
  });
}

/**
 * Fired once when a persuasion section on the long premium page first becomes
 * visible, so content engagement / scroll-through can be measured per section
 * (which slates are seen before the reader drops off).
 */
export function trackPremiumSectionView(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  sectionId: string;
  marketCountry?: string | null;
}): void {
  trackPremiumAnalyticsEvent('premium_section_view', {
    checkout_surface: params.checkoutSurface,
    section_id: params.sectionId,
    market_country: params.marketCountry || undefined
  });
}

/**
 * Fired once per crossed depth milestone (25/50/75/100) so non-converter
 * scroll behavior on the long page is measurable independent of any click.
 */
export function trackPremiumScrollDepth(params: {
  checkoutSurface: TPremiumCheckoutSurface;
  percentScrolled: number;
  marketCountry?: string | null;
}): void {
  trackPremiumAnalyticsEvent('premium_scroll_depth', {
    checkout_surface: params.checkoutSurface,
    percent_scrolled: params.percentScrolled,
    market_country: params.marketCountry || undefined
  });
}
